import { Theme, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import React from 'react';
import { useSelector } from 'react-redux';
import { useAppSelector, useShallowSelector } from '../../app/hooks';
import { selectOrderValues } from '../../selectors/order';
import { activeCartSelector } from '../../utils/cart';
import Colors from '../../utils/color';
import { printCurrency } from '../../utils/money';
import { usePOSCart } from '../../hooks/usePOSCart.hook';
import {
  POS_CART_SUBTOTAL_PRICE_INTENT,
  POS_CART_TOTAL_PRICE_INTENT,
} from '../../utils/constants';
import { AMOUNT_DEFAULTER } from '../../constants';

// HITL 3.0 order-total card. Same behavior as the OLD OrderTotalContainer,
// restyled with full-width / inset dividers and a lighter 16px Total to match
// the Agent Cart card.
const useStyles = makeStyles((theme: Theme) => ({
  container: {
    width: '100%',
    // A little top room; the horizontal padding lives on the rows so the inset
    // Total divider aligns with the card padding. The pinned-footer card's own
    // top border separates the totals from the stack above, so there is no
    // full-width divider above Subtotal here (it read as a double line).
    padding: theme.spacing(0.5, 0, 0),
    backgroundColor: Colors.white,
    '& div': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      '& .MuiTypography-root': {
        fontSize: '12px',
      },
    },
    '& > div': {
      padding: theme.spacing(0.25, 1.5),
    },
    // Inset divider above Total (drawn as a pseudo-element so it doesn't span
    // the full width like the Subtotal line).
    '& > div:last-child': {
      position: 'relative',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: theme.spacing(1.5),
        right: theme.spacing(1.5),
        borderTop: `${theme.spacing(0.25)} solid ${Colors.alto}`,
      },
      '& .MuiTypography-root': {
        fontSize: '16px',
        fontWeight: 600,
      },
    },
  },
  itemRowContent: {
    '&:hover': {
      backgroundColor: '#BFD7ED',
      cursor: 'pointer',
    },
  },
}));

export default function OrderTotalCard() {
  const classes = useStyles();
  const ITEM_BY_ITEM = useAppSelector((state) => state.config.ITEM_BY_ITEM);
  const { subtotal, tax, total } = useShallowSelector(selectOrderValues);
  const cartActive = useSelector(activeCartSelector);
  const { onOrderTotalClick, setTimestamp } = usePOSCart();

  if (!cartActive) {
    return <></>;
  }

  const getPrice = (amount: number | null) =>
    ITEM_BY_ITEM && amount !== null
      ? printCurrency(amount, 'USD')
      : AMOUNT_DEFAULTER;
  const subtotalLine = getPrice(subtotal);
  const taxLine = getPrice(tax);
  const totalLine = getPrice(total);

  return (
    <div id="cartTotalContainer" className={classes.container}>
      <div
        {...(subtotalLine !== AMOUNT_DEFAULTER && {
          className: classes.itemRowContent,
          onMouseDown: setTimestamp,
          onMouseUp: () =>
            onOrderTotalClick(POS_CART_SUBTOTAL_PRICE_INTENT, subtotal || 0),
        })}
      >
        <Typography>Subtotal:</Typography>
        <Typography>{subtotalLine}</Typography>
      </div>
      <div>
        <Typography>Tax:</Typography>
        <Typography>{taxLine}</Typography>
      </div>
      <div
        {...(totalLine !== AMOUNT_DEFAULTER && {
          className: classes.itemRowContent,
          onMouseDown: setTimestamp,
          onMouseUp: () =>
            onOrderTotalClick(POS_CART_TOTAL_PRICE_INTENT, total || 0),
        })}
      >
        <Typography>Total:</Typography>
        <Typography>{totalLine}</Typography>
      </div>
    </div>
  );
}
